/**
 * Feed daily-goal streak flow — web port of iOS FeedHeaderProgress
 * (client-native/src/features/top-headers/FeedHeaderProgress.tsx:39-126).
 *
 * Drives the TopBar listening bar from the server (`getDailyListening`, polled
 * every 30s, so listening done on other devices counts too). On crossing the
 * user's daily goal while `canClaimStreakToday`, it fires the
 * feed_listening_goal_reached analytics event (streak_days + user_level, as
 * iOS does), claims the streak via `postIncrementStreak`, then 1s later shows
 * CompletionCelebration → full-screen FeedStreakScreen. Tapping the bar opens
 * the Daily Listening Goal picker.
 *
 * Several TopBars can be mounted at once (the vertical carousel keeps the
 * previous/next panels alive), so the crossing detector and the flow state
 * live at module scope, and exactly one mounted instance — the "overlay
 * owner" — renders the modal layers.
 */
import { useEffect, useRef } from "react";
import { observable } from "@legendapp/state";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { callWithAuth, getUserStats } from "@/api/Api";
import { useUser } from "@/hooks/UseAuthProviders";
import { useUserSettings } from "@/hooks/useUserSettings";
import { useTrackFeedEvents } from "../../hooks/useTrackFeedEvents";
import { getDailyListening, postIncrementStreak } from "./streakApi";

/** Default daily listening goal when the user hasn't set one (iOS default: 5 min). */
export const DEFAULT_GOAL_MINUTES = 5;

export const showCelebration$ = observable(false);
export const showStreakScreen$ = observable(false);
export const showGoalPicker$ = observable(false);

// iOS previousListeningTimeRef — module-scoped so the concurrently mounted
// TopBar instances share one crossing detector (the first effect to observe a
// crossing advances it; the others then see previous === current).
let previousListeningTime = 0;

// Overlay-owner registry: the first mounted instance renders the modals.
let nextInstanceId = 0;
const mountedInstances: number[] = [];
const overlayOwnerId$ = observable<number | null>(null);

export interface UseFeedStreakReturn {
  currentListeningTime: number;
  targetListeningTime: number;
  isOverlayOwner: boolean;
  openGoalPicker: () => void;
  closeGoalPicker: () => void;
  handleCelebrationDismiss: () => void;
  handleStreakContinue: () => void;
  handleGoalChange: (minutes: number) => void;
}

export function useFeedStreak(): UseFeedStreakReturn {
  const { trackFeedListeningGoalReached } = useTrackFeedEvents();
  const { user } = useUser();
  const { settings, updateSettings } = useUserSettings();
  const queryClient = useQueryClient();

  // Read goal from user settings (server-synced), default 5 minutes.
  const targetListeningTime =
    (settings?.dailyListeningGoalMinutes || DEFAULT_GOAL_MINUTES) * 60;

  const { data: dailyListeningData } = useQuery({
    queryKey: ["dailyListening"],
    queryFn: () => callWithAuth(getDailyListening),
    refetchInterval: 30000, // Poll every 30s while the feed is mounted
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const { data: userStats } = useQuery({
    queryKey: ["userStats"],
    queryFn: () => callWithAuth(getUserStats),
  });

  const currentListeningTime = dailyListeningData?.dailyListeningSeconds || 0;
  const canClaimStreakToday = dailyListeningData?.canClaimStreakToday ?? true;

  const { mutate: incrementStreak } = useMutation({
    mutationFn: () => callWithAuth(postIncrementStreak),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userStats"] });
      queryClient.invalidateQueries({ queryKey: ["dailyListening"] });
      setTimeout(() => {
        showCelebration$.set(true);
      }, 1000);
    },
    onError: (error) => {
      console.error("Error incrementing streak:", error);
    },
  });

  // Overlay-owner registration.
  const instanceIdRef = useRef<number | null>(null);
  if (instanceIdRef.current === null) instanceIdRef.current = nextInstanceId++;
  useEffect(() => {
    const id = instanceIdRef.current as number;
    mountedInstances.push(id);
    if (overlayOwnerId$.peek() === null) overlayOwnerId$.set(id);
    return () => {
      const i = mountedInstances.indexOf(id);
      if (i >= 0) mountedInstances.splice(i, 1);
      if (overlayOwnerId$.peek() === id) {
        overlayOwnerId$.set(
          mountedInstances.length > 0 ? mountedInstances[0] : null,
        );
      }
    };
  }, []);

  // Goal-crossing detector (iOS FeedHeaderProgress.tsx:74-103). Track with the
  // current streak (before incrementing) and user level.
  const userLevel = user?.placementResult;
  const dayStreak = userStats?.dayStreak;
  useEffect(() => {
    const crossedThreshold =
      previousListeningTime < targetListeningTime &&
      currentListeningTime >= targetListeningTime;

    if (crossedThreshold && canClaimStreakToday) {
      trackFeedListeningGoalReached(
        currentListeningTime,
        targetListeningTime,
        dayStreak,
        userLevel,
      );
      incrementStreak();
    }

    previousListeningTime = currentListeningTime;
  }, [
    currentListeningTime,
    targetListeningTime,
    canClaimStreakToday,
    incrementStreak,
    trackFeedListeningGoalReached,
    dayStreak,
    userLevel,
  ]);

  return {
    currentListeningTime,
    targetListeningTime,
    isOverlayOwner: overlayOwnerId$.get() === instanceIdRef.current,
    openGoalPicker: () => showGoalPicker$.set(true),
    closeGoalPicker: () => showGoalPicker$.set(false),
    handleCelebrationDismiss: () => {
      showCelebration$.set(false);
      showStreakScreen$.set(true);
    },
    handleStreakContinue: () => {
      showStreakScreen$.set(false);
    },
    handleGoalChange: (minutes: number) => {
      void updateSettings({ dailyListeningGoalMinutes: minutes });
    },
  };
}
